import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ─── ConfirmDialog ───────────────────────────────────────────────────────────
//
// Ersetzt window.confirm() durch einen gestalteten Dialog im Stil des
// DeleteTreeModal. Nutzung über den Promise-basierten Hook:
//
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: 'Person löschen',
//     message: 'Diese Aktion kann nicht rückgängig gemacht werden.',
//     variant: 'danger',
//   });
//   if (!ok) return;
//
// Der ConfirmDialogProvider muss einmal oberhalb des Routers gemountet sein.

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** Beschriftung des Bestätigen-Buttons (Default: „Bestätigen"). */
  confirmLabel?: string;
  /** Beschriftung des Abbrechen-Buttons (Default: „Abbrechen"). */
  cancelLabel?: string;
  /** 'danger' → rotes Warn-Icon und roter Bestätigen-Button. */
  variant?: 'danger' | 'default';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm() benötigt einen ConfirmDialogProvider oberhalb im Baum.');
  }
  return confirm;
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending((prev) => {
        // Falls bereits ein Dialog offen ist: den alten als „abgebrochen" auflösen.
        prev?.resolve(false);
        return { options, resolve };
      });
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setPending((prev) => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialogView
          options={pending.options}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

// ─── Dialog-Ansicht ──────────────────────────────────────────────────────────

interface ConfirmDialogViewProps {
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialogView({ options, onConfirm, onCancel }: ConfirmDialogViewProps) {
  const { title, message, confirmLabel = 'Bestätigen', cancelLabel = 'Abbrechen', variant = 'default' } = options;
  const isDanger = variant === 'danger';
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Fokus sicher auf „Abbrechen" (verhindert versehentliches Bestätigen per
  // Enter) und beim Schließen an das auslösende Element zurückgeben – wie
  // es window.confirm() implizit tat.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  // Modales Tastatur-Verhalten (capture-Phase am document):
  // - Escape → abbrechen
  // - Tab → Fokus zyklisch zwischen den Dialog-Buttons halten (Fokusfalle)
  // - alle übrigen Tasten nicht zur Seite dahinter durchlassen (sonst feuern
  //   dort Hotkey-Listener wie Pfeiltasten-Navigation oder Cmd+K weiter).
  //   Enter/Space aktivieren den fokussierten Button weiterhin nativ über
  //   das separate click-Event.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        const focusables = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>('button') ?? [],
        );
        if (focusables.length === 0) return;
        const index = focusables.indexOf(document.activeElement as HTMLElement);
        const next = e.shiftKey
          ? (index - 1 + focusables.length) % focusables.length
          : (index + 1) % focusables.length;
        focusables[next]?.focus();
        return;
      }
      e.stopPropagation();
    }
    document.addEventListener('keydown', handleKey, true);
    return () => document.removeEventListener('keydown', handleKey, true);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        ref={dialogRef}
        className={`w-full max-w-md rounded-lg border bg-white p-6 shadow-xl ${
          isDanger ? 'border-red-200' : 'border-slate-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              isDanger ? 'bg-red-100' : 'bg-blue-100'
            }`}
          >
            {isDanger ? (
              <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
              </svg>
            )}
          </div>
          <h2 id="confirm-dialog-title" className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
        </div>

        {message && <div className="mb-5 text-sm text-slate-600">{message}</div>}

        <div className="flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isDanger
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
